import { BooleanTristateValue, ConfigurationAlert, ConfigurationAlertType, JsonReply, Partaker, SelectOption, SelectOptionGroup } from "@grupo-constant/next-dtype/types";

import { TaskMenuActionEnum, TaskStatusEnum, TaskTrackingEventEnum } from "../tasks/tasks.types";
import { TaskTypeTypology } from "../../../core/task-type/task-type.types";
import { LocaleString } from "../../../core/translate/localeString.model";

export interface Workshift {
    workshiftId: string;
    marca_code: string;
    cliente_code: string;
    projectId: string;
    serviceId: string;
    belongsTo: string;
    taskTypeId: string;
    taskTypeUri: string;
    taskTypeName: string;
    taskTypeTypology: TaskTypeTypology;
    externalWorkshiftId: string;
    workplaceCode: string;
    workplaceName: string;
    workplaceVicinity: string;
    workplacePlaceId: string;
    workplaceUri: string;
    workplaceTimeZoneId: string;
    requiredJobs: WorkshiftJob[];
    resources: WorkshiftResource[];
    scheduled_from_date: string;
    scheduled_to_date: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    startWorkshiftDate: string;
    endWorkshiftDate: string;
    status: WorkshiftStatusEnum;
    trackingEvent: WorkshiftTrackingEventEnum;
    timestamp: string;
    delayedStart: boolean;
    delayedEnd: boolean;
    minutesBySchedule: number;
    minutesReal: number;
    startedWithoutGeoposition: BooleanTristateValue;
    finishedWithoutGeoposition: BooleanTristateValue;
    activationDate: string;
    notice: any;
    noticeUri: string;
    serverPayload: WorkshiftPayload;
    alertType: ConfigurationAlertType;
    alert: ConfigurationAlert[];
    cancellationReasonCode: string;
    cancellationReasonDescription: LocaleString;
    rejectedReasonCode: string;
    rejectedReasonDescription: LocaleString;
}

export interface SearchWorkshiftElement {
    workshift: SearchWorkshiftElementWorkshift;
    group: SearchWorkshiftElementGroup;
    schedule: SearchWorkshiftElementSchedule;
    who: SearchWorkshiftElementWho;
    status: SearchWorkshiftElementStatus;
    alert: ConfigurationAlert[];
    action: TaskMenuActionEnum[];
    showReason?: boolean;
    showInfo?: boolean;
    timeRemainingToStart?: number;
    timeRemainingToEnd?: number;
}

export interface SearchWorkshiftElementWorkshift {
    workshiftId: string;
    marca_code: string;
    cliente_code: number;
    projectId: string;
    serviceId: string;
    externalWorkshiftId: string;
    belongsTo: string;
    taskTypeId: string;
    taskTypeUri: string;
    taskTypeName: string;
    taskTypeTypology: TaskTypeTypology;
}

export interface SearchWorkshiftElementGroup {
    supervisors: string[];
    workplaceUri: string;
    workplaceCode: string;
    workplaceName: string;
    workplaceVicinity: string;
    workplaceTimeZoneId: string;
}

export interface SearchWorkshiftElementSchedule {
    from: string;
    to: string;
    timeFrom: string;
    timeTo: string;
    fromDateVisibility: string;
    endDateVisibility: string;
    startDate: string;
    endDate: string;
    delayedStart: boolean;
    delayedEnd: boolean;
    minutesBySchedule: number;
    minutesReal: number;
}

export interface SearchWorkshiftElementWho {
    requiredJobs: WorkshiftJob[];
    resources: WorkshiftResource[];
}

export interface SearchWorkshiftElementStatus {
    status: TaskStatusEnum;
    trackingEvent: TaskTrackingEventEnum;
    timestamp: string;
    activationDate: string;
    cancellationReason: LocaleString;
    rejectedReason: LocaleString;
    startedWithoutGeoposition: BooleanTristateValue;
    finishedWithoutGeoposition: BooleanTristateValue;
}

export interface WorkshiftJob {
    workshiftId: string;
    jobName: string;
    value: number;
}

export interface WorkshiftRequiredJob {
    job: string;
    planned: number | undefined;
    selected: number | undefined;
}
	
export interface WorkshiftResource {
    workshiftId: string;
    workshiftDate: string;
    userId: string;
    userName: string;
    weekMinutes: number;
    dayMinutes: number;
    jobs: string[];
    assigned: boolean;
    isExpanded?: boolean;
}

export interface WorkshiftPayload {
    service: {
        serviceId: string;
        name: string;
        dateFrom: string;
        dateTo: string;
    };
}

export interface RetrieveWorkshiftResourcesRequest {
    serviceId: string;
    workshiftId?: string;
    date: string;
    workplaceCode: string;
}

export interface RetrieveWorkshiftResourcesReply extends JsonReply {
    workshiftResoruces?: WorkshiftResource[];
    partaker: Partaker;
}

export interface FlushWorkshiftRequest {
    workshiftId?: string;
    serviceId: string;
    taskTypeId: string;
    workplaceCode: string;
    externalWorkshiftId?: string;
    scheduled_from_date: string;
    scheduled_to_date?: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    notice?: any;
    extraPayload?: any;
    workshiftJobs: WorkshiftJob[];
    workshiftResources: WorkshiftResource[];
}

export interface FlushWorkshiftReply extends JsonReply {
    workshift: Workshift;
    partaker: Partaker;
}

export interface FlushWorkshiftModelRequest {
    modelId?: string;
    serviceId: string;
    eventModel: FlushWorkshiftModel;
    eventType: 'WORKSHIFT';
    periodicity: Periodicity;
}

export interface FlushWorkshiftModel {
    serviceId: string;
    taskTypeId: string;
    workplaceCode: string;
    externalWorkshiftId?: string;
    scheduled_from_time: string;
    scheduled_to_time: string;
    notice?: any;
    extraPayload?: any;
    workshiftJobs: WorkshiftJob[];
    workshiftResources: WorkshiftResource[];
}

export interface FlushWorkshiftModelReply extends JsonReply {
    searchPeriodicElement: SearchWorkshiftModelElement;
    partaker: Partaker;
}

export interface SearchWorkshiftModelElement {
    workshift: SearchWorkshiftModelElementWorkshift;
    group: SearchWorkshiftModelElementGroup;
    schedule: SearchWorkshiftModelElementSchedule;
    who: SearchWorkshiftModelElementWho;
    periodicity: Periodicity;
    model: SearchWorkshiftModelElementModel;
}

export interface SearchWorkshiftModelElementWorkshift {
    marca_code: string;
    cliente_code: number;
    projectId: string;
    serviceId: string;
    externalWorkshiftId: string;
    belongsTo: string;
    taskTypeId: string;
    taskTypeUri: string;
    taskTypeName: string;
    taskTypeTypology: TaskTypeTypology;
}

export interface SearchWorkshiftModelElementGroup {
    supervisors: string[];
    workplaceUri: string;
    workplaceCode: string;
    workplaceName: string;
    workplaceVicinity: string;
    workplaceTimeZoneId: string;
}

export interface SearchWorkshiftModelElementSchedule {
    from: string;
    to: string;
    timeFrom: string;
    timeTo: string;
    fromDateVisibility: string;
    endDateVisibility: string;
    startDate: string;
    endDate: string;
    delayedStart: boolean;
    delayedEnd: boolean;
    minutesBySchedule: number;
    minutesReal: number;
}

export interface SearchWorkshiftModelElementWho {
    requiredJobs: WorkshiftJob[];
    resources: WorkshiftResource[];
}

export interface SearchWorkshiftModelElementModel {
    currentIteration: string;
    lastIteration: string;
    modelId: string;
    nextIteration: string;
    status: WorkshiftPeriodicityStatusEnum;
    eventStatus: WorkshiftPeriodicityStatusEnum;
    modelStatusEvents: WorkshiftModelStatusEvent[];
}

export interface WorkshiftModelStatusEvent {
    eventStatus: WorkshiftPeriodicityStatusEnum;
    modelId: string;
    serviceId: string;
    timestamp: string;
    status: WorkshiftPeriodicityStatusEnum;
    userId: string;
    userName: string;
}

export interface Periodicity {
    periodicityType: WorkshiftPeriodicityTypeEnum;
    periodicityTypeInterval: number;
    weekDays?: WorkshiftWeekDaysEnum[];
    monthDay?: number;
    orderDay?: number;
    orderWeekDay?: WorkshiftWeekDaysEnum;
    startDate: string;
    advanceCreationDays: number;
    endPeriodicityType: WorkshiftEndPeriodicityTypeEnum;
    endDate?: string;
    endIteration?: number;
}

export interface SearchWorkshiftRequest {
    serviceId: string;
    workplaceCode: string[];
    handwrittenId: string[];
    taskTypeId: string[];
    from?: string;
    to?: string;
    from_time?: string;
    to_time?: string;
    statuses: TaskStatusEnum[];
    reason: string[];
    chunksize?: number;
    lastConsumedWorkshiftId?: string;
    count: boolean;
    orderBy: string;
    descending: boolean;
}

export interface SearchWorkshiftModelRequest {
    serviceId: string;
    eventType: 'WORKSHIFT';
    onlyActiveEvents: boolean;
    chunksize?: number;
    lastConsumedModelId?: string;
    count: boolean;
    orderBy: string;
    descending: boolean;
    workplaceCode: string[];
    oid: string[];
    eventStatus: WorkshiftPeriodicityStatusEnum[];
}

export interface SearchWorkshiftModelReply extends JsonReply {
    searchPeriodicModelElement: SearchWorkshiftModelElement[];
    partaker: Partaker;
    size: number;
    count?: number;
    eof: boolean;
}

export interface WorkshiftEventSummary {
    event: TaskTrackingEventEnum;
    timestamp: string;
    ack: string;
    rc: string;
    userId: string;
    userName: string;
    payload: any;
}

export interface WorkshiftFormSummary {
    formUri: string;
    caption: LocaleString;
    oid: string;
    creationDate: string;
    queueDate: string;
    receivedDate: string;
}

export interface WorkshiftUserEventSummary {
    workshiftId: string;
    workplaceCode: string;
    externalWorkshiftId?: string;
    serviceId: string;
    userId: string;
    userName: string;
    workshiftDate: string;
    checkInTimestamp: string | undefined;
    checkOutTimestamp: string | undefined;
    workMinutes: number;
    pauseMinutes: number;
    maximumPauseMinutes?: number;
}

export interface WorkshiftUserEvent {
    workshiftId: string;
    workplaceCode: string;
    serviceId: string;
    event: WorkshiftUserEventTypeEnum;
    userId: string;
    date: string;
    queueTimestamp: string;
    receivedTimestamp: string;
}

export enum WorkshiftUserEventTypeEnum {
    CHECKIN = 'CHECKIN',
    CHECKOUT = 'CHECKOUT'
}
export interface SearchWorkshiftReply extends JsonReply {
    workshift: SearchWorkshiftElement[];
    partaker: Partaker;
    size: number;
    count?: number;
    eof: boolean;
}

export interface RetrieveWorkshiftSearchOptionsRequest {
    marca_code?: string;
    cliente_code?: number;
    projectId?: string;
    serviceId: string;
}

export interface RetrieveWorkshiftSearchOptionsReply extends JsonReply {
    availableSearchOptions: WorkshiftSearchOptionsFilterEnum[];
    taskType: SelectOptionGroup[];
    workplaces: SelectOption[];
    schedule: {
        from: string;
        to: string;
    };
    statuses: SelectOption[];
    reason: SelectOption[];
}

export interface RetrieveWorkshiftByIdRequest {
    workshiftId: string;
    serviceId: string;
    includeWorkshiftTracking: boolean;
    includeWorkshiftFormSummary: boolean;
    includeWorkshiftUserEventSummary: boolean;
}

export interface RetrieveWorkshiftByIdReply extends JsonReply {
    workshift: Workshift;
    row: SearchWorkshiftElement;
    workshiftMessaging: WorkshiftEventSummary[];
    workshiftTracking: WorkshiftEventSummary[];
    workshiftFormSummary: WorkshiftFormSummary[];
    partaker: Partaker;
}

export interface BeautifyWorkshiftEventRequest {
    serviceId: string;
    workplaceCode: string;
    workshiftId: string;
    userId: string;
    eventSummary: WorkshiftUserEventSummary;
}

export interface BeautifyWorkshiftEventReply extends JsonReply {
    eventSummary: WorkshiftUserEventSummary;
    daySummary: WorkshiftUserEventSummary;
    partaker: Partaker;
}

export interface SendWorkshiftListRequest {
    workshiftId: string[];
    serviceId: string;
}

export interface SendWorkshiftListReply extends JsonReply {
    unchangedWorkshiftId?: string[];
}

export interface CancelWorkshiftListRequest {
    workshiftId: string[];
    serviceId: string;
    cancellationReason: string;

}

export interface CancelWorkshiftListReply extends JsonReply {
    unchangedWorkshiftId?: string[];
}

export interface ResetWorkshiftListRequest {
    workshiftId: string[];
    serviceId: string;
}

export interface ResetWorkshiftListReply extends JsonReply {
    unchangedWorkshiftId?: string[];
}

export interface ForceWorkshiftStartEndRequest {
    serviceId: string;
    workshiftId: string;
    start: boolean;
}

export interface DeleteWorkshiftRequest {
    workshiftId: string[];
    serviceId: string;
}

export interface DeleteWorkshiftReply extends JsonReply {
    notDeletedWorkshiftId?: string[];
}

export interface FinishPeriodicEventRequest {
    modelId: string;
    serviceId: string;
    cleanFutureEvents: boolean;
}

export interface RetrieveWorkplaceEventSummaryRequest {
    serviceId: string;
    date: string;
    workplaceCode: string;
}

export interface RetrieveWorkplaceEventSummaryReply extends JsonReply {
    summaryByWorkshift: { [workshift: string]: WorkshiftUserEventSummary };
    daySummary: WorkshiftUserEventSummary[];
}

export interface RetrieveWorkshiftUserEventListRequest {
    serviceId: string;
    workshiftId: string;
    userId: string;
}

export interface RetrieveWorkshiftUserEventListReply extends JsonReply {
    events?: WorkshiftUserEvent[];
}

export enum WorkshiftStatusEnum {
    WITHERROR = 'WITHERROR',
    PLANNED = 'PLANNED',
    SENT = 'SENT',
    ACK = 'ACK',
    RC = 'RC',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    MOVED = 'WITHEMOVEDRROR',
    MOVING = 'MOVING',
    PAUSED = 'PAUSED',
    WORKING = 'WORKING',
    PREVIEW = 'PREVIEW',
    DONE = 'DONE',
    DONE_WITH_ERROR = 'DONE_WITH_ERROR',
    SUSPENDED = 'SUSPENDED'
}

export enum WorkshiftTrackingEventEnum {
    AUTO_START = 'AUTO_START',
    WITHERROR = 'WITHERROR',
    PLANNED = 'PLANNED',
    SENT = 'SENT',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    WORKING = 'WORKING',
    PAUSED = 'PAUSED',
    DONE = 'DONE',
    DONE_WITH_ERROR = 'DONE_WITH_ERROR',
    FORM_RECEIVED = 'FORM_RECEIVED',
    RESET = 'RESET',
    DELAYED_START = 'DELAYED_START',
    DELAYED_END = 'DELAYED_END',
    DELAYED_START2 = 'DELAYED_START2',
    DELAYED_END2 = 'DELAYED_END2',
    MANUAL_SENT = 'MANUAL_SENT',
    SMS_SENT_START = 'SMS_SENT_START',
    SMS_SENT_END = 'SMS_SENT_END',
    SENT_ACK = 'SENT_ACK',
    SENT_RC = 'SENT_RC',
    UPDATED = 'UPDATED',
    MESSAGE = 'MESSAGE',
    SUSPENDED = 'SUSPENDED'
}

export enum WorkshiftSearchOptionsFilterEnum {
    CUSTOMER = 'CUSTOMER',
    PROJECT = 'PROJECT',
    WORKPLACE = 'WORKPLACE',
    WORKSHIFT_ID = 'WORKSHIFT_ID',
    DATE_RANGE = 'DATE_RANGE',
    HOUR_RANGE = 'HOUR_RANGE',
    STATUS = 'STATUS',
    REASON = 'REASON'
}

export enum WorkshiftSearchOptionsReasonEnum {
    DELAYED_START = 'DELAYED_START',
    DELAYED_END = 'DELAYED_END',
    STARTED_WITHOUT_GEOPOSITION = 'STARTED_WITHOUT_GEOPOSITION',
    FINISHED_WITHOUT_GEOPOSITION = 'FINISHED_WITHOUT_GEOPOSITION',
    WITHOUT_ASSIGNED_RESOURCE = 'WITHOUT_ASSIGNED_RESOURCE',
    WITHOUT_ASSIGNED_PERSON = 'WITHOUT_ASSIGNED_PERSON',
    WITHOUT_ASSIGNED_HORARY = 'WITHOUT_ASSIGNED_HORARY',
}

export enum WorkshiftPeriodicityStatusEnum {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    FINISHED = 'FINISHED',
    PLANNING = 'PLANNING',
    CANCELLED = 'CANCELLED'
}

export enum WorkshiftPeriodicityTypeEnum {
    DAYS = 'DAYS',
    WEEKS = 'WEEKS',
    MONTHS = 'MONTHS',
    YEARS = 'YEARS'
}

export enum WorkshiftEndPeriodicityTypeEnum {
    DATE = 'DATE',
    ITERATIONS = 'ITERATIONS'
}

export enum WorkshiftWeekDaysEnum {
    MONDAY = 'MONDAY',
    TUESDAY = 'TUESDAY',
    WEDNESDAY = 'WEDNESDAY',
    THURSDAY = 'THURSDAY',
    FRIDAY = 'FRIDAY',
    SATURDAY = 'SATURDAY',
    SUNDAY = 'SUNDAY'
}
